import { useState } from "react";

import { EntryList } from "./EntryList";

import type { SearchFilterSetItem } from "~/client/components/search/hooks/useSavedSearchFilterSets";
import { useSaveSearchFilterSet } from "~/client/components/search/hooks/useSaveSearchFilterSet";
import type { SearchHistoryEntry } from "~/client/components/search/hooks/useSearchHistory";
import { SearchFilterSetSavingFeedback } from "~/client/components/search/SearchFilterSetSavingFeedback";
import { useDeleteSearchHistoryEntryMutation } from "~/client/generated/schema";
import type { ListItemSelection } from "~/client/hooks/useSelectListItemByKey";
import type { Connection } from "~/client/utils/relay";

export type SearchHistoryEntryWithPersistency = SearchHistoryEntry & { isFilterSetSaved: boolean };

export const buildSearchHistoryEntryWithPersistency = (
  origin: ReadonlyArray<SearchHistoryEntry>,
  filterSetItems: Connection<SearchFilterSetItem>,
): ReadonlyArray<SearchHistoryEntryWithPersistency> => {
  return origin.map((entry) => {
    const isFilterSetSaved = filterSetItems.edges.some((e) => e.node.contentHash === entry.filterSetHash);
    return { ...entry, isFilterSetSaved };
  });
};

interface Props {
  listIndex: number;
  entries: ReadonlyArray<SearchHistoryEntry>;
  listItemSelection: ListItemSelection;
  searchFilterSetItems: Connection<SearchFilterSetItem>;
}
export const EntryListContainer: React.FC<Props> = ({
  listIndex,
  entries,
  listItemSelection,
  searchFilterSetItems,
}) => {
  const [deletedEntryIds, setDeletedEntryIds] = useState<Array<string>>([]);
  const [deleteSearchHistoryEntry] = useDeleteSearchHistoryEntryMutation();
  const [handleSaveFilterSet, { data, loading, error }] = useSaveSearchFilterSet();

  const handleDelete = (id: string) => {
    deleteSearchHistoryEntry({ variables: { input: { id } } });
    setDeletedEntryIds([...deletedEntryIds, id]);
  };

  const currentEntries = entries.filter((e) => !deletedEntryIds.includes(e.id));

  return (
    <>
      <EntryList
        listIndex={listIndex}
        entries={buildSearchHistoryEntryWithPersistency(currentEntries, searchFilterSetItems)}
        listItemSelection={listItemSelection}
        onSaveFilterSet={handleSaveFilterSet}
        onDelete={handleDelete}
      />
      <SearchFilterSetSavingFeedback data={data} loading={loading} error={error} />
    </>
  );
};
